/**
 * DateField and DateRangeFilter, on Carbon DatePicker / DatePickerInput. Values are plain `yyyy-mm-dd` strings (empty
 * string for no date), the same format the filters and URL params already use; they are read and written in local time.
 *
 * DateField props: `id`, `labelText`, `value`, `onChange(value)`, `invalid`, `invalidText`, `helperText`, `onBlur`.
 * DateRangeFilter props: `id`, `legendText`, `from`, `to`, `onChange({ from, to })`, optional `fromLabel`, `toLabel`
 * (default: the i18n From / To), and `inline` for a row of filters: no fieldset legend, so its labels line up with the
 * fields beside it (the labels then name the range themselves, e.g. "Period from").
 */
import { DatePicker, DatePickerInput, FormGroup } from '@carbon/react';
import { useTranslation } from '@pos/i18n';

import { toDateValue } from './dates';

const DATE_FORMAT = 'Y-m-d';
// Carbon's default pattern is the US m/d/Y; ours is ISO, or the browser would refuse to submit the form.
const DATE_PATTERN = '\\d{4}-\\d{2}-\\d{2}';

export function DateField({
  id,
  labelText,
  value,
  onChange,
  invalid,
  invalidText,
  helperText,
  onBlur,
}: {
  id: string;
  labelText: string;
  value: string;
  onChange(value: string): void;
  invalid?: boolean;
  invalidText?: string;
  helperText?: string;
  onBlur?(): void;
}) {
  const { t } = useTranslation();
  return (
    <DatePicker
      datePickerType="single"
      dateFormat={DATE_FORMAT}
      value={value}
      onChange={(dates: Date[]) => onChange(dates[0] ? toDateValue(dates[0]) : '')}
    >
      <DatePickerInput
        id={id}
        labelText={labelText}
        placeholder={t('admin.datePlaceholder')}
        pattern={DATE_PATTERN}
        invalid={invalid}
        invalidText={invalidText}
        helperText={helperText}
        onBlur={onBlur}
      />
    </DatePicker>
  );
}

export interface DateRange {
  from: string;
  to: string;
}

export function DateRangeFilter({
  id,
  legendText,
  from,
  to,
  onChange,
  fromLabel,
  toLabel,
  inline = false,
}: {
  id: string;
  legendText: string;
  from: string;
  to: string;
  onChange(range: DateRange): void;
  fromLabel?: string;
  toLabel?: string;
  inline?: boolean;
}) {
  const { t } = useTranslation();
  const picker = (
    <DatePicker
      datePickerType="range"
      dateFormat={DATE_FORMAT}
      value={[from, to].filter(Boolean)}
      onChange={(dates: Date[]) =>
        onChange({ from: dates[0] ? toDateValue(dates[0]) : '', to: dates[1] ? toDateValue(dates[1]) : '' })
      }
    >
      <DatePickerInput
        id={`${id}-from`}
        labelText={fromLabel ?? t('admin.filters.from')}
        placeholder={t('admin.datePlaceholder')}
        pattern={DATE_PATTERN}
      />
      <DatePickerInput
        id={`${id}-to`}
        labelText={toLabel ?? t('admin.filters.to')}
        placeholder={t('admin.datePlaceholder')}
        pattern={DATE_PATTERN}
      />
    </DatePicker>
  );
  return inline ? picker : <FormGroup legendText={legendText}>{picker}</FormGroup>;
}
